import { Paragraph } from './Paragraph';
import { ISegment, IParagraph } from './interfaces';
import { makeAutoObservable } from 'mobx';

/**
 * Segment represents a continuous speech by a single speaker
 * It contains metadata about the speaker and a list of paragraphs
 */
export class Segment implements ISegment {
  /** Unique identifier for the segment */
  id: string;
  
  /** ID of the speaker for this segment */
  speakerLabel: string;
  
  /** Start time of the segment in seconds */
  startTime: number;
  
  /** End time of the segment in seconds */
  endTime: number;
  
  /** List of paragraphs in the segment */
  paragraphs: Paragraph[];

  /** Counter for the segment */
  counter: number;
  
  /** Maps paragraphId to index in the paragraphs array for quick lookup */
  private paragraphMap: Map<string, number>;
  
  /**
   * Creates a new Segment
   * 
   * @param id Unique identifier for the segment
   * @param speakerLabel Label of the speaker for this segment
   * @param startTime Start time of the segment in seconds
   * @param endTime End time of the segment in seconds
   * @param paragraphs List of paragraphs in the segment
   */
  constructor(
    id: string,
    speakerLabel: string,
    startTime: number,
    endTime: number,
    paragraphs: Paragraph[] = []
  ) {


    this.id = id;
    this.speakerLabel = speakerLabel;
    this.startTime = startTime;
    this.endTime = endTime;
    this.paragraphs = paragraphs;
    this.counter = 0;
    // Build the paragraph lookup map
    this.paragraphMap = new Map();
    this.rebuildParagraphMap();

    // Enable reactivity on dynamic operations.
    makeAutoObservable(this);
  }
  
  /**
   * Rebuilds the paragraph lookup map
   * Call this after any operations that modify the paragraphs array
   */
  private rebuildParagraphMap(): void {
    this.paragraphMap.clear();
    this.paragraphs.forEach((paragraph, index) => {
      this.paragraphMap.set(paragraph.id, index);
    });
    this.counter++;
  }
  
  /**
   * Gets a child entity by ID (implements ParentEntity)
   */
  getChild(paragraphId: string): Paragraph | undefined {
    return this.getParagraph(paragraphId);
  }
  
  /**
   * Adds a child entity (implements ParentEntity)
   */
  addChild(paragraph: Paragraph): void {
    this.addParagraph(paragraph);
  }
  
  /**
   * Removes a child entity (implements ParentEntity)
   */
  removeChild(paragraphId: string): boolean {
    return this.removeParagraph(paragraphId);
  }
  
  /**
   * Updates a child entity (implements ParentEntity)
   */
  updateChild(paragraphId: string, updatedParagraph: Paragraph): boolean {
    return this.updateParagraph(paragraphId, updatedParagraph);
  }
  
  /**
   * Gets a paragraph by ID
   * 
   * @param paragraphId ID of the paragraph to get
   * @returns The paragraph, or undefined if not found
   */
  getParagraph(paragraphId: string): Paragraph | undefined {
    const index = this.paragraphMap.get(paragraphId);
    return index !== undefined ? this.paragraphs[index] : undefined;
  }
  
  /**
   * Adds a paragraph to the segment
   * Updates segment timing if necessary
   * 
   * @param paragraph Paragraph to add
   */
  addParagraph(paragraph: Paragraph): void {
    // Update parent references
    paragraph.segmentId = this.id;
    
    // Add to paragraphs array
    this.paragraphs.push(paragraph);
    this.paragraphMap.set(paragraph.id, this.paragraphs.length - 1);
    
    // Update segment timing if necessary
    this.updateTimingFromParagraphs();
    
    // Ensure parent-child references are maintained
    this.updateInternalData();
    this.counter++;
  }

  /** 
   * Adds multiple paragraphs to the segment
   * Updates segment timing if necessary
   * 
   * @param paragraphs Array of paragraphs to add
   */
  addParagraphs(paragraphs: Paragraph[]): void {
    this.paragraphs.push(...paragraphs);
    this.rebuildParagraphMap();
    
    // Update segment timing if necessary
    this.updateTimingFromParagraphs();
    
    // Ensure parent-child references are maintained
    this.updateInternalData();
    this.counter++;
  }

  /**
   * Updates internal data making sure all children are referring to proper parent
   */
  updateInternalData(): void {
    this.paragraphs.forEach(paragraph => {
      paragraph.updateInternalData(this.id, this.speakerLabel);
    });
    this.counter++;
  }
  
  /**
   * Removes a paragraph from the segment
   * 
   * @param paragraphId ID of the paragraph to remove
   * @returns true if the paragraph was removed, false if not found
   */
  removeParagraph(paragraphId: string): boolean {
    const index = this.paragraphMap.get(paragraphId);
    if (index === undefined) {
      return false;
    }
    
    this.paragraphs.splice(index, 1);
    this.rebuildParagraphMap();
    
    // Update segment timing
    this.updateTimingFromParagraphs();
    this.counter++;
    return true;
  }
  
  /**
   * Updates a paragraph in the segment
   * 
   * @param paragraphId ID of the paragraph to update
   * @param updatedParagraph Updated paragraph data
   * @returns true if the paragraph was updated, false if not found
   */
  updateParagraph(paragraphId: string, updatedParagraph: Paragraph): boolean {
    const index = this.paragraphMap.get(paragraphId);
    if (index === undefined) {
      return false;
    }
    
    // Ensure parent references are maintained
    updatedParagraph.segmentId = this.id;
    
    // Update paragraph
    this.paragraphs[index] = updatedParagraph;
    
    // Update segment timing
    this.updateTimingFromParagraphs();
    
    // Update internal data for this paragraph
    updatedParagraph.updateInternalData(this.id, this.speakerLabel);
    this.counter++;
    return true;
  }
  
  /**
   * Replaces a paragraph in the segment with a new one
   * This differs from updateParagraph in that it accepts a different paragraph ID
   * 
   * @param oldParagraphId ID of the paragraph to replace
   * @param newParagraph The new paragraph to insert
   * @returns true if the paragraph was replaced, false if the old paragraph was not found
   */
  replaceParagraph(oldParagraphId: string, newParagraph: Paragraph): boolean {
    const index = this.paragraphMap.get(oldParagraphId);
    if (index === undefined) {
      return false;
    }
    
    // Ensure parent references are maintained
    newParagraph.segmentId = this.id;
    
    // Replace the paragraph at the same position
    this.paragraphs[index] = newParagraph;
    
    // Rebuild the map because the ID has changed
    this.rebuildParagraphMap();
    
    // Update segment timing
    this.updateTimingFromParagraphs();
    
    // Update internal data for this paragraph
    newParagraph.updateInternalData(this.id, this.speakerLabel);
    this.counter++;
    return true;
  }
  
  /**
   * Gets the index of a paragraph in the paragraphs array
   * 
   * @param paragraphId ID of the paragraph to find
   * @returns The index of the paragraph, or -1 if not found
   */
  getParagraphIndex(paragraphId: string): number {
    const index = this.paragraphMap.get(paragraphId);
    return index !== undefined ? index : -1;
  }
  
  /**
   * Updates the segment's start and end times based on its paragraphs
   */
  private updateTimingFromParagraphs(): void {
    if (this.paragraphs.length === 0) {
      return;
    }
    
    // Filter out paragraphs with invalid timing values
    const validTimingParagraphs = this.paragraphs.filter(p => 
      typeof p.startTime === 'number' && 
      typeof p.endTime === 'number' && 
      !isNaN(p.startTime) && 
      !isNaN(p.endTime)
    );
    
    if (validTimingParagraphs.length === 0) {
      return;
    }
    
    // Find the earliest start time and latest end time
    let earliestStart = Number.MAX_VALUE;
    let latestEnd = 0;
    
    for (const paragraph of validTimingParagraphs) {
      if (paragraph.startTime < earliestStart) {
        earliestStart = paragraph.startTime;
      }
      if (paragraph.endTime > latestEnd) {
        latestEnd = paragraph.endTime;
      }
    }
    
    // Ensure we found valid times
    if (earliestStart !== Number.MAX_VALUE) {
      this.startTime = earliestStart;
    }
    
    if (latestEnd > 0) {
      this.endTime = latestEnd;
    }
    
    // Ensure start time is not greater than end time
    if (this.startTime > this.endTime) {
      // If timing is invalid, add a small buffer to make duration valid
      this.endTime = this.startTime + 0.001;
    }
    this.counter++;
  }
  
  /**
   * Updates the speaker for this segment
   * 
   * @param speakerLabel New speaker label
   */
  updateSpeaker(speakerLabel: string): void {
    this.speakerLabel = speakerLabel;
    
    // Update all child paragraphs with the new speaker
    this.updateInternalData();
    this.counter++;
  }
  
  /**
   * Gets the duration of the segment in seconds
   */
  get duration(): number {
    return this.endTime - this.startTime;
  }
  
  /**
   * Gets all paragraphs that overlap with a given time range
   * 
   * @param startTime Start time of the range in seconds
   * @param endTime End time of the range in seconds
   * @returns Array of paragraphs that overlap with the range
   */
  getParagraphsByTimeRange(startTime: number, endTime: number): Paragraph[] {
    return this.paragraphs.filter(paragraph => 
      (paragraph.startTime >= startTime && paragraph.startTime <= endTime) ||
      (paragraph.endTime >= startTime && paragraph.endTime <= endTime)
    );
  }
  
  /**
   * Creates a clone of this segment with new IDs
   * @param preserveId Whether to preserve the original segment ID
   * @param preserveParagraphIds Whether to preserve the original paragraph IDs
   * @param preserveElementIds Whether to preserve the original element IDs
   * @returns A new segment with cloned paragraphs
   */
  cloneWithNewIds(
    preserveId: boolean = false, 
    preserveParagraphIds: boolean = false,
    preserveElementIds: boolean = false
  ): Segment {

    // Generate new ID or preserve existing one
    const segmentId = preserveId 
      ? this.id 
      : `seg-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`;
    
    // Clone the segment with the appropriate ID
    const clonedSegment = new Segment(
      segmentId,
      this.speakerLabel,
      this.startTime,
      this.endTime,
      [] // Empty paragraphs array at first
    );
    
    // Clone all paragraphs with appropriate IDs
    const clonedParagraphs = this.paragraphs.map(paragraph => 
      paragraph.cloneWithNewIds(segmentId, preserveParagraphIds, preserveElementIds)
    );
    
    // Add cloned paragraphs to the segment
    clonedSegment.paragraphs = clonedParagraphs;
    clonedSegment.rebuildParagraphMap();
    
    return clonedSegment;
  }
  
  /**
   * Serializes the segment to JSON
   */
  toJSON(): Record<string, any> {
    return {
      id: this.id,
      speakerLabel: this.speakerLabel,
      startTime: this.startTime,
      endTime: this.endTime,
      paragraphs: this.paragraphs.map(paragraph => paragraph.toJSON())
    };
  }
  
  /**
   * Creates a Segment from a JSON object
   * 
   * @param json JSON object to deserialize
   * @returns A new Segment instance
   */
  static fromJSON(json: any): Segment {
    const segment = new Segment(
      json.id,
      json.speakerLabel,
      json.startTime,
      json.endTime,
      []
    );
    
    // Deserialize paragraphs
    segment.paragraphs = json.paragraphs.map((paragraphJson: any) => 
      Paragraph.fromJSON(paragraphJson)
    );
    
    // Rebuild the paragraph map
    segment.rebuildParagraphMap();
    
    // Ensure parent-child references are maintained
    segment.updateInternalData();
    
    return segment;
  }

  /**
   * Increments the counter for the segment
   */
  incrementCounter(): void {
    this.counter++;
  }
}